import * as React from "react";
import { Accordion } from "../../../../../../../react-core/Html/Accordion";
import { CircleBtn, CircleBtnType } from "../../../../../../../react-core/Html/CircleBtn";
import { BankAccountOwner } from "../../../../../../../Wrap/Constants/Enums/Domain/BankAccountOwner";
import { BankAccount } from "../../../../../../../Wrap/Models/Domains/BankAccount/BankAccount";
import { BankAccountInstruction } from "../../../../../../../Wrap/Models/Domains/Instructions/BankAccountInstruction";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import AriaTooltip from "../../../../../../Shared/Tooltip/AriaTooltip";
import { PaymentType } from "../../Resource/Contents";
import { SectionHeader } from "../../SharedComponents";
import { BankAccount as BankAccountComponent } from "./BankAccount";
import { NewBankAccountInstruction } from "./NewBankAccountInstruction";

interface IAdditionalBankAccountProps extends IProcessComponentProps {
    bankAccounts: BankAccount[];
    bankAccountInstructions: BankAccountInstruction[];
    onSaveNewBankAccountInstruction?: (BankAccountInstruction: BankAccountInstruction) => void;
    onRemoveBankAccountInstruction?: (BankAccountInstruction: BankAccountInstruction) => void;
    inUseBankAccountInstruction?: string[];
}

interface IAdditionalBankAccountState {
    addNewBankAccount: boolean;
    validationMssage: string[];
}

export class AdditionalBankAccount extends ProcessComponent<IAdditionalBankAccountProps, IAdditionalBankAccountState> {
    constructor(props: any) {
        super(props);
        this.state = { addNewBankAccount: false, validationMssage: [] };
    }

    isValid(): boolean {
        const { addNewBankAccount } = this.state;
        this.setState({ validationMssage: [] });
        if (addNewBankAccount) {
            this.setState({
                validationMssage: ["Please save bank account."],
            });
            return false;
        }
        return super.isValid();
    }

    public render() {
        const bankAccountInstructions = this.props.bankAccountInstructions || [];
        const bankAccounts = this.props.bankAccounts || [];
        const bankNum = bankAccountInstructions.length + bankAccounts.length;
        const maxBankNum = 4;
        const { validationMssage } = this.state;
        return (
            <>
                {validationMssage.map((m, i) => (
                    <Alert key={i} alertType={AlertType.Error} message={m}></Alert>
                ))}
                {SectionHeader("Add bank details for SIPP/Bond")}
                {bankNum < maxBankNum && this.renderAddNewBankAccount()}
                {this.renderBankAccounts(bankAccounts)}
                {this.renderBankAccountInstructions(bankAccountInstructions)}
            </>
        );
    }

    private renderAddNewBankAccount = () => {
        return (
            <>
                {!this.state.addNewBankAccount && (
                    <>
                        <CircleBtn
                            type={CircleBtnType.add}
                            title="Add new Bank account"
                            onClick={() => this.setState({ addNewBankAccount: true })}
                        />
                        <span>
                            <AriaTooltip cssWrapper="is-info-icon ml-6" showCloseButton={true}>
                                The bank accounts that have been added so far are shown below or if you require an
                                additional bank account to be available for SIPP and or Bond payments in or out please
                                enter it here
                            </AriaTooltip>
                        </span>
                    </>
                )}
            </>
        );
    };

    private renderBankAccounts = (bankAccounts: BankAccount[]) => {
        return (
            <>
                {bankAccounts.map((bankAccount) => (
                    <>
                        <Accordion
                            key={`Bank account`}
                            label={`Bank account`}
                            cssWrapper="accordion-simple bigger-arrow"
                            isOpen={true}
                        >
                            <BankAccountComponent data={bankAccount} />
                        </Accordion>
                    </>
                ))}
            </>
        );
    };

    private getPaymentByBankAccountOwner(bankAccountInstruction: BankAccountInstruction): PaymentType {
        if (bankAccountInstruction.Owner == BankAccountOwner.ThirdParty) {return PaymentType.ThirdParty;}
        else if (bankAccountInstruction.Owner == BankAccountOwner.Employer) {return PaymentType.Employer;}
        else {return PaymentType.Client;}
    }

    private renderBankAccountInstructions(bankAccountInstructions: BankAccountInstruction[]) {
        return (
            <>
                {bankAccountInstructions.map((bankAccountInstruction) => (
                    <>
                        <Accordion
                            key={`Bank account`}
                            label={`Bank account`}
                            cssWrapper="accordion-simple bigger-arrow"
                            isOpen={true}
                        >
                            <NewBankAccountInstruction
                                onChange={() => {}}
                                data={bankAccountInstruction}
                                onRef={(ref) => this.appendChild(ref)}
                                payeeType={this.getPaymentByBankAccountOwner(bankAccountInstruction)}
                                readonly={true}
                            />

                            {!(this.props.inUseBankAccountInstruction || []).some(
                                (item) => item == bankAccountInstruction.AccountNumber
                            ) && (
                                <CircleBtn
                                    type={CircleBtnType.remove}
                                    title="Remove this bank account"
                                    onClick={() => this.onRemoveBankAccountInstruction(bankAccountInstruction)}
                                />
                            )}
                        </Accordion>
                    </>
                ))}
                {this.state.addNewBankAccount && this.renderNewBankAccountInstruction(PaymentType.Client)}
            </>
        );
    }

    private renderNewBankAccountInstruction(payeeType: PaymentType) {
        return (
            <>
                <CircleBtn
                    cssWrapper="mt-20"
                    type={CircleBtnType.remove}
                    title="Cancel add bank account"
                    onClick={() => {
                        this.setState({ addNewBankAccount: false });
                        this.setState({
                            validationMssage: [],
                        });
                    }}
                />
                <span>
                    <AriaTooltip cssWrapper="is-info-icon ml-6" showCloseButton={true}>
                        The bank accounts that have been added so far are shown below or if you require an
                        additional bank account to be available for SIPP and or Bond payments in or out please
                        enter it here
                    </AriaTooltip>
                </span>

                <NewBankAccountInstruction
                    onChange={(instruction) => {
                        this.onSaveNewBankAccountInstruction(instruction);
                    }}
                    data={null}
                    onRef={(ref) => this.appendChild(ref)}
                    payeeType={payeeType}
                    isReview={false}
                />
            </>
        );
    }

    private onRemoveBankAccountInstruction(bankAccountInstruction: BankAccountInstruction) {
        const { bankAccountInstructions } = this.props;
        const index = bankAccountInstructions.findIndex((x) => x.AccountNumber == bankAccountInstruction.AccountNumber);
        if (index != -1) {
            this.props.onRemoveBankAccountInstruction(bankAccountInstruction);
        }
    }

    private onSaveNewBankAccountInstruction(bankAccountInstruction: BankAccountInstruction) {
        const { bankAccountInstructions, onSaveNewBankAccountInstruction } = this.props;
        if (bankAccountInstructions.findIndex((x) => x.AccountNumber == bankAccountInstruction.AccountNumber) == -1) {
            onSaveNewBankAccountInstruction(bankAccountInstruction);
            this.setState({ addNewBankAccount: false });
            this.setState({
                validationMssage: [],
            });
        } else {
            return this.props.showErrorMsgPopUp(["Please enter a non-existing bank account"], true);
        }
    }
}
